/* E-Sign Consent — required by the federal ESIGN Act of 2000 (15 U.S.C.
   §7001) and the Uniform Electronic Transactions Act (UETA). Confirms
   that every electronic signature, click-to-accept, or e-confirmation
   on the Platform is legally binding the same as a wet-ink signature. */
import { Link } from "react-router-dom";
import { PenTool, ArrowLeft } from "lucide-react";

export default function Esign() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-esign">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <PenTool className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Electronic Signature &amp; Communications Consent</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective May 1, 2026</div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <p>
            By creating a verqo account or clicking "I agree" on any verqo
            agreement, you consent to conducting business with us electronically
            under the federal Electronic Signatures in Global and National
            Commerce Act (ESIGN Act, 15 U.S.C. §7001) and any applicable state
            Uniform Electronic Transactions Act (UETA).
          </p>

          <h2 className="display text-xl mt-8 mb-2">1. What you're agreeing to</h2>
          <ul>
            <li>Your electronic signature (a typed name, click-to-accept, OTP confirmation, or check-box) is the legal equivalent of a handwritten ("wet-ink") signature.</li>
            <li>Disclosures, agreements, statements, notices, tax documents (1099-K, 1099-NEC, etc.), and any other communications we are legally required to provide may be delivered <em>electronically</em> — typically by email, in-platform notice, or downloadable PDF.</li>
            <li>You are responsible for keeping the email on your account current. Notices are deemed received when sent.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">2. System requirements</h2>
          <ul>
            <li>A modern web browser (latest 2 versions of Chrome, Firefox, Safari, or Edge).</li>
            <li>JavaScript enabled, cookies enabled, TLS 1.2+ supported.</li>
            <li>An active email account that can receive plain-text and HTML email.</li>
            <li>Software capable of viewing and printing PDF documents (Adobe Reader, browser-native, etc.).</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">3. Withdrawing consent</h2>
          <p>
            You may withdraw your consent to receive electronic communications
            at any time by emailing <a href="mailto:privacy@verqo.com" className="underline">privacy@verqo.com</a>. However, the
            Platform fundamentally relies on electronic delivery — withdrawing
            consent will result in verqo closing your account, since we cannot
            operate using paper mail. We will continue to provide any required
            tax documents (e.g., 1099-K) via the last known method permitted by law.
          </p>

          <h2 className="display text-xl mt-8 mb-2">4. Requesting paper copies</h2>
          <p>
            You may request a paper copy of any agreement or required disclosure
            by emailing <a href="mailto:legal@verqo.com" className="underline">legal@verqo.com</a>. We may charge a reasonable
            handling fee for paper copies (currently US$10 per document) to cover
            printing and postage.
          </p>

          <h2 className="display text-xl mt-8 mb-2">5. Updates</h2>
          <p>
            If we materially change this consent (e.g., new types of records
            delivered electronically), we will notify you in advance and ask
            for re-acknowledgement.
          </p>

          <p>
            See also: <Link to="/legal/terms" className="underline">Terms of Service</Link> · <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
